/**
 * Navigations-Bruecke zwischen einbettender Seite (Parent) und eingebetteter
 * App (Child) — die vierte neben Theme, Aenderung und Flaeche.
 *
 * ── WARUM ES SIE BRAUCHT ────────────────────────────────────────────────────
 *
 * Eine App im rechten Seitenpanel hat KEINE eigene Huelle mehr: kein TopNav,
 * keine Seitenleiste, kein Handy-Kopf (`useIsEmbedded` + `embed-vorpaint.ts`).
 * Seit dem 2026-09-21 gilt das unabhaengig von der Panelbreite — vorher hing die
 * Leiste an `md:`, und weil das im iframe die PANELBREITE misst, stand sie in
 * der Standardstufe (800 px) da und trug den App-Namen ein zweites Mal.
 *
 * Damit fehlt dem Nutzer dort genau EIN Weg, den er in der App sonst hat: „eine
 * andere Seite der App". Betreiber, 2026-09-18: „dass wir irgendwie oben in den
 * eingefuegten Header … eine Menue ist, wo ich dann halt in das mobile Menue
 * gehen kann, halt auch die ganzen Seiten zu oeffnen".
 *
 * ── UND WARUM KEIN ZURUECK ──────────────────────────────────────────────────
 *
 * Es gab bis zum 2026-09-21 einen zweiten Befehl, `zurueck`, und einen Pfeil dafuer im
 * Panel-Kopf — aus derselben Meldung („oder einen Zurueck Button zu haben").
 * Beides ist am 2026-09-21 wieder entfallen, und die drei Gruende stehen hier,
 * damit es nicht als Luecke zurueckkommt:
 *
 *  1. **Er war doppelt.** Gezaehlt ueber die acht Apps, die als Panel-Artefakt
 *     erreichbar sind: von 34 Unterrouten tragen **25 einen eigenen
 *     Rueckweg** — in der Seite selbst oder in einem ihrer Bausteine
 *     (`DetailView`, `AgentForm`, `WorkflowBuilderClient` …). Der Pfeil im
 *     Panel-Kopf stand also in drei von vier Faellen 56 px ueber einem zweiten,
 *     der dasselbe tut. Betreiber, 2026-09-21: „obwohl er schon im Content ist,
 *     beispielsweise bei Aufgaben war das glaube ich, oder bei Kontakte."
 *  2. **Er war stumm tot, sobald der WIRT zwischendurch navigiert hatte.** Am
 *     2026-09-21 im Showroom-Panel gemessen: Kind navigiert (`/contacts` →
 *     `/contacts/merge`), Wirt legt einen eigenen Eintrag auf, dann der Pfeil —
 *     **weder Wirt noch Gast bewegen sich**, waehrend derselbe Rueckschritt vom
 *     Wirt aus sehr wohl greift (Kontrollzelle). Das iframe traegt
 *     `sandbox="allow-scripts allow-same-origin allow-forms allow-popups
 *     allow-modals"`, also KEIN `allow-top-navigation`; eine Traversierung, die
 *     einen Vorfahren bewegen wuerde, wird verworfen. Ohne fremden Schritt
 *     davor funktionierte er (`/contacts/merge` → `/contacts`) — der Knopf war
 *     also nicht kaputt, sondern unzuverlaessig, und das ist schlechter.
 *  3. **Er konnte sein Ziel nicht nennen.** `history.back()` weiss nicht, wohin;
 *     der Rueckweg der Seite tut es (`router.push("/contacts")`) und beschriftet
 *     sich danach.
 *
 * Wer ihn zurueckholen will, braucht fuer (2) eine Antwort — und die kann nicht
 * „nur anzeigen, wenn das Kind einen Rueckweg hat" sein, denn genau so war es.
 * Die neun Unterrouten ohne eigenen Rueckweg erreichen ihre Liste seither ueber
 * das Menue (ein Klick mehr): contacts/merge, calendar/booking/availability,
 * automations/meldungen/[id], drive/text/[nodeId], drive/ansehen/[nodeId],
 * immo/objekte/[id]/bearbeiten, immo/objekte/neu, immo/suchprofile/[id],
 * mail-client/newsletter/[id].
 *
 * ── WARUM postMessage UND NICHT DER DIREKTE GRIFF ───────────────────────────
 *
 * `iframe.contentWindow.…` aus dem Einbetter heraus wirft: die eingebettete App
 * liegt auf einer anderen Subdomain, also einer anderen Origin. Bleibt dieselbe
 * Mechanik wie bei den drei Nachbarn — fester Nachrichtenschluessel,
 * geschlossene Werteliste, aufgeloeste Ziel-Origin, nie `"*"`, unbekannte
 * Nachricht VERWORFEN.
 *
 * ── DIE NACHRICHT IST EIN BEFEHLSWORT, KEIN ZIEL ────────────────────────────
 *
 * Erlaubt ist genau ein Wort. Ein `path`/`url`/`href` ist ausdruecklich
 * verboten — woertlich dieselbe Zusage wie in `embed-refresh.ts`: ein Pfad im
 * Protokoll machte daraus ein Navigations-Primitiv und damit einen
 * Redirect-Vektor in ein iframe, dessen Inhalt der Nutzer fuer seine App haelt.
 * Wirkung: selbst wenn eine Nachricht durch beide Origin-Pruefungen rutscht,
 * kann sie hoechstens das eigene Menue der App aufklappen. Sie kann niemals
 * bestimmen, WOHIN.
 *
 * ── UND EINE GEGENRICHTUNG, DIE DIE ANDEREN DREI NICHT HABEN ────────────────
 *
 * Ein Knopf, der nichts tut, ist schlimmer als keiner. Nur das Kind weiss, ob es
 * ueberhaupt Seiten fuehrt — also meldet es das, und der Kopf zeigt bis dahin
 * NICHTS. Kein Blindflug, kein Flackern, und eine App mit aelterem gui-elements
 * bekommt schlicht keinen Knopf statt eines toten.
 */

/**
 * Der eine Befehl. Feste Liste statt „was reinkommt" — und eine Liste bleibt es,
 * obwohl nur ein Wort darin steht: `istEmbedNavBefehl` prueft gegen sie, und ein
 * String-Vergleich an ihrer Stelle waere beim zweiten Wort wieder aufzubauen.
 *
 * **`menue-umschalten` heisst so und nicht `menue`.** Der Wirt weiss NICHT, ob
 * das Schubfach gerade offen ist — der Nutzer kann es im Kind ueber den
 * Schleier geschlossen haben, ohne dass darueber etwas gemeldet wuerde. Ein
 * Befehl `oeffne` behauptete dieses Wissen; `schalte um` laesst den Zustand
 * dort, wo er lebt.
 *
 * Und es ist kein Namensdetail, sondern der einzige sichtbare Rueckweg aus dem
 * Menue: eingebettet blendet `AppLayout` die Handy-Kopfzeile aus, in der sonst
 * derselbe Knopf als Kreuz steht (`MobileMenuButton`). Bliebe der Befehl
 * „oeffnen", koennte man das Schubfach nur durch Danebentippen wieder
 * loswerden — im schmalen Panel eine Sackgasse.
 */
export const EMBED_NAV_BEFEHLE = ["menue-umschalten"] as const;

export type EmbedNavBefehl = (typeof EMBED_NAV_BEFEHLE)[number];

export interface EmbedNavBefehlMessage {
  __janewayEmbedNavigation: EmbedNavBefehl;
}

/**
 * Was das Kind ueber sich meldet — mehr gibt es nicht zu wissen.
 *
 * ══ EIN FELD, UND ES WIRD GEMELDET STATT GERATEN ═══════════════════════════
 *
 * Wer diese Bruecke spaeter „vereinfachen" will, faengt hier an: der Wirt
 * koennte den Menueknopf doch einfach immer zeigen. Kann er nicht — eine App
 * ohne Navigationsabschnitte oeffnet dann ein leeres Schubfach, und eine App mit
 * aelterem gui-elements gar keines. Ein Knopf, der nichts tut, ist schlimmer als
 * keiner, und nur das Kind kennt seine `sections`.
 *
 * ══ UND WARUM HIER KEIN `kannZurueck` MEHR STEHT ═══════════════════════════
 *
 * Bis zum 2026-09-21 trug die Meldung ein zweites Feld fuer den Rueckweg. Es ist mit
 * dem Pfeil im Panel-Kopf entfallen; die drei Messungen dazu stehen im Kopf
 * dieser Datei. Der Grund, warum es ueberhaupt ein gemeldetes Feld war, bleibt
 * aber lesenswert, denn er gilt fuer jedes kuenftige:
 *
 * Gemessen am 2026-09-18 in headless Chrome — eine Wirtsseite mit EINEM iframe,
 * beide gleicher Origin, `history.length` auf beiden Seiten gelesen:
 *
 *                              Wirt   Kind
 *     Start                      2      2
 *     der WIRT pusht einmal      3      3     ← das Kind waechst mit,
 *     das KIND pusht einmal      4      4        ohne irgendwo gewesen zu sein
 *
 * Ein iframe sieht die GEMEINSAME Sitzungsgeschichte des ganzen Tabs, nicht
 * seine eigene. Wer im Wirt etwas ueber das Kind ABLEITEN will, leitet es aus
 * Zahlen ab, in denen er selbst steckt. Deshalb meldet das Kind, was es weiss —
 * und nur das.
 *
 * Waechter: `src/__tests__/embed-navigation-bridge.test.tsx`.
 */
export interface EmbedNavZustand {
  /** Fuehrt die App Seiten, die ein Menue anbieten koennte? */
  hatMenue: boolean;
}

export interface EmbedNavZustandMessage {
  /**
   * `kannZurueck` steht NUR fuer Wirte da, die gegen ein gui-elements von vor
   * dem 2026-09-21 gebaut sind; deren Leser verlangt beide Felder. Es ist `false` und
   * wird beim Lesen abgeschnitten — Begruendung und Abbruchbedingung bei
   * `postEmbedNavZustand`.
   */
  __janewayEmbedNavZustand: EmbedNavZustand & { kannZurueck?: false };
}

/**
 * Query-Parameter, mit dem der Einbetter seine eigene Origin an die Anfrage
 * haengt — die Adresse, an die das Kind zurueckmelden darf.
 *
 * ── WARUM AN DER ADRESSE UND NICHT AUS DER NACHRICHT ────────────────────────
 *
 * Das Kind muss melden, BEVOR der Wirt etwas fragt: der `onLoad`-Post des
 * Einbetters geht gemessen 141–150 ms bevor das Kind ueberhaupt einen
 * Empfaenger hat (`embed-flaeche.ts`, Kopf). Ein Rueckkanal, der auf eine
 * eingehende Nachricht wartet, haette dasselbe Rennen.
 *
 * `document.referrer` faellt aus: das iframe traegt `referrerPolicy="no-referrer"`
 * (`AppEmbedArtifact.tsx`). Und `"*"` ist hier so wenig erlaubt wie ueberall
 * sonst in dieser Bruecken-Familie.
 *
 * Bleibt der Weg, den `flaeche` und `farbthema` schon gehen: der Wert reist mit
 * der ANFRAGE. Er kommt damit aus einer Adresse, die ein Fremder setzen kann —
 * deshalb prueft das Kind ihn gegen dieselbe Gebietsliste, gegen die es auch
 * Absender prueft (`isAllowedEmbedOrigin`, `EMBED_PARENT_ORIGIN_SUFFIXES`), und
 * das Schlimmste, was ein Treffer erreicht, ist EIN Wahrheitswert an eine andere
 * Janeway-App.
 */
export const EMBED_WIRT_PARAM = "wirt";

export function istEmbedNavBefehl(wert: unknown): wert is EmbedNavBefehl {
  return typeof wert === "string" && (EMBED_NAV_BEFEHLE as readonly string[]).includes(wert);
}

export function isEmbedNavBefehlMessage(data: unknown): data is EmbedNavBefehlMessage {
  return istEmbedNavBefehl((data as EmbedNavBefehlMessage | null)?.__janewayEmbedNavigation);
}

/**
 * Die Meldung des Kindes lesen — und dabei auf genau den einen Wahrheitswert
 * ZUSCHNEIDEN.
 *
 * Nicht nur pruefen, sondern neu bauen: was sonst noch in der Nachricht steht,
 * kaeme mit dem Objekt in den Zustand des Einbetters und von dort in dessen
 * Baum. `null` heisst „keine brauchbare Meldung" — der Kopf zeigt dann weiter
 * nichts, statt zu raten.
 *
 * **Ein Feld zuwenig ist auch keine brauchbare Meldung.** Ein Kind mit aelterem
 * gui-elements schickt `{kannZurueck, hatMenue}`; `hatMenue` liest sich daraus
 * richtig, das zweite Feld faellt hier ab. Ein Kind, das UEBERHAUPT kein
 * `hatMenue` schickt, bekommt `null` — und damit keinen Knopf statt eines toten.
 */
export function leseNavZustand(data: unknown): EmbedNavZustand | null {
  const roh = (data as EmbedNavZustandMessage | null)?.__janewayEmbedNavZustand;
  if (!roh || typeof roh !== "object") return null;
  if (typeof roh.hatMenue !== "boolean") return null;
  return { hatMenue: roh.hatMenue };
}

/**
 * Parent-Seite: schickt dem eingebetteten iframe den Befehl.
 *
 * `targetOrigin` ist die aufgeloeste Ziel-Origin, **nie `"*"`** — woertlich
 * dieselbe Zusage wie `postEmbeddedTheme`, `postEmbeddedRefresh` und
 * `postEmbeddedFlaeche`.
 */
export function postEmbeddedNavBefehl(
  frame: HTMLIFrameElement | null,
  targetOrigin: string,
  befehl: EmbedNavBefehl,
): void {
  const win = frame?.contentWindow;
  if (!win) return;
  if (!targetOrigin || targetOrigin === "*") return;
  if (!istEmbedNavBefehl(befehl)) return;
  const message: EmbedNavBefehlMessage = { __janewayEmbedNavigation: befehl };
  win.postMessage(message, targetOrigin);
}

/**
 * Child-Seite: meldet dem Wirt, was der Kopf anbieten darf.
 *
 * ── WARUM `kannZurueck: false` MITGESCHICKT WIRD, OBWOHL ES DAS FELD NICHT
 *    MEHR GIBT ──────────────────────────────────────────────────────────────
 *
 * Weil Wirt und Gast in VERSCHIEDENEN Repos liegen und nie gleichzeitig neu
 * gebaut werden. Der Leser von vor dem 2026-09-21 verlangt BEIDE Wahrheitswerte
 * und gibt sonst `null` zurueck — ein Gast, der schon umgestellt ist, nimmt einem Wirt,
 * der es noch nicht ist, damit den Menueknopf. Stumm: kein Fehler, keine Luecke,
 * der Knopf ist einfach weg, und die App im Panel hat dann gar keine Navigation
 * mehr (die Seitenleiste ist ja im selben Zug entfallen).
 *
 * Es sind drei Wirte — `artifact/AppEmbedArtifact.tsx`, der GUI-Showroom und
 * `EmbeddedApp.tsx` in janeway-messenger — und rund zehn Gaeste. Die Reihenfolge
 * des Ausrollens waere sonst eine Bringschuld, die niemand prueft.
 *
 * **Ende dieser Zeile:** wenn kein Wirt mehr laeuft, der gegen ein gui-elements
 * ohne diese Aenderung gebaut wurde. Entscheidend ist der BAU, nicht das Datum:
 * die Bibliothek wird beim Bau der App mit hineinkompiliert
 * (`transpilePackages`), ein Container von gestern traegt also den Leser von
 * gestern. Gegenprobe ohne Raten: im Panel `hatMenue` melden lassen und sehen,
 * ob der Menueknopf erscheint.
 */
export function postEmbedNavZustand(
  wirtFenster: Window | null,
  targetOrigin: string,
  zustand: EmbedNavZustand,
): void {
  if (!wirtFenster) return;
  if (!targetOrigin || targetOrigin === "*") return;
  const message: EmbedNavZustandMessage = {
    __janewayEmbedNavZustand: { hatMenue: zustand.hatMenue, kannZurueck: false },
  };
  wirtFenster.postMessage(message, targetOrigin);
}

/**
 * Eine REINE Origin — `https://host[:port]`, sonst nichts.
 *
 * Der Wert wird spaeter als `targetOrigin` benutzt. Ein Pfad oder eine Abfrage
 * daran laesst `postMessage` werfen, und ein `javascript:`- oder `data:`-Wert
 * hat in einer Ziel-Origin ohnehin nichts verloren. Geprueft wird ueber die
 * Rueckrechnung: was der Parser als `origin` ausgibt, muss Zeichen fuer Zeichen
 * das sein, was hereinkam.
 */
export function istWirt(wert: unknown): wert is string {
  if (typeof wert !== "string" || !wert) return false;
  try {
    const url = new URL(wert);
    if (url.protocol !== "https:" && url.protocol !== "http:") return false;
    return url.origin === wert;
  } catch {
    return false;
  }
}

/** Child-Seite: die Origin des Wirts aus der eigenen Adresse lesen. */
export function wirtAusUrl(search: string): string | null {
  const wert = new URLSearchParams(search).get(EMBED_WIRT_PARAM);
  return istWirt(wert) ? wert : null;
}

/** Parent-Seite: den Parameter an eine Einbettungs-Adresse haengen. */
export function mitWirtInDerUrl(url: string, wirt: string | null): string {
  if (!istWirt(wirt)) return url;
  const trenner = url.includes("?") ? "&" : "?";
  return `${url}${trenner}${EMBED_WIRT_PARAM}=${encodeURIComponent(wirt)}`;
}
